<script>
    import { onMount } from 'svelte';
    import { writable } from 'svelte/store';
    import { currentUser, selectedItem, redovisning, answer, question } from '../../store/store.js';
    import { fetchBrf, getFileName } from '../../utils/utils.js';
    import Header from '../../components/Header.svelte';
    import TopBar from '../../components/TopBar.svelte';
    import Logotype from '../../components/Logotype.svelte';
    import { Link } from "svelte-routing";
    import Search from '../../components/Search.svelte';

    // Import all the analysis components
    import General from '../../components/boxes/redovisning/General.svelte';
    import Chatbot from '../../components/Bobot.svelte';
    import Rating from '../../components/Rating.svelte';
    import Property from '../../components/boxes/redovisning/Property.svelte';
    import Maintenance from '../../components/boxes/redovisning/Maintenance.svelte';
    import EconomyAndIncomes from '../../components/boxes/redovisning/EconomyAndIncomes.svelte';
    import Company from '../../components/boxes/Company.svelte';
    import UploadRedovisning from '../../components/boxes/UploadRedovisning.svelte';
    import PropertyMap from '../../components/PropertyMap.svelte';
    import LoanFeeHistory from '../../components/boxes/history/LoanFeeHistory.svelte';
    import ExpensesHistory from '../../components/boxes/history/ExpensesHistory.svelte';
    import IncomeHistory from '../../components/boxes/history/IncomeHistory.svelte';
    // Stadgar
    import Membership from '../../components/boxes/stadgar/Membership.svelte';
    import OwnershipTransfer from '../../components/boxes/stadgar/OwnershipTransfer.svelte';
    import RightsObligations from '../../components/boxes/stadgar/RightsObligations.svelte';
    import MaintenancePlan from '../../components/boxes/stadgar/MaintenancePlan.svelte';
    import ForverkanOwner from '../../components/boxes/stadgar/ForverkanOwner.svelte';
    import RevenueDisposition from '../../components/boxes/stadgar/RevenueDisposition.svelte';
    import StadgarEtc from '../../components/boxes/stadgar/StadgarEtc.svelte';
    import Liquidation from '../../components/boxes/stadgar/Liquidation.svelte';
    import Stamma from '../../components/boxes/stadgar/Stamma.svelte';
    import Styrning from '../../components/boxes/stadgar/Styrning.svelte';
    import OwnerRights from '../../components/boxes/stadgar/OwnerRights.svelte';
    import Fees from '../../components/boxes/stadgar/Fees.svelte';
    import AnalyzeStadgar from '../../components/AnalyzeStadgar.svelte';
    
    // Energideklaration
    import EnergyGeneral from '../../components/boxes/energi/EnergyGeneral.svelte';
    import BuildingInfo from '../../components/boxes/energi/BuildingInfo.svelte';
    import SunEnergy from '../../components/boxes/energi/SunEnergy.svelte';
    import Heating from '../../components/boxes/energi/Heating.svelte';
    import Suggestions from '../../components/boxes/energi/Suggestions.svelte';
    import AnalyzeEnergy from '../../components/AnalyzeEnergy.svelte';
    
    // This is how we get the ID parameter from svelte-routing
    export let id;
    let loading = true;
    let error = null;
    let activeTab = writable('allmant'); // Tracks the active tab: 'allmant', 'stadgar', or 'energi'

    // Helper function to create URL-friendly slugs
    function slugify(text) {
        return text
            .toString()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^\w-]+/g, '')
            .replace(/--+/g, '-');
    }

    let showIncomeHistory = false;
    let showLoanFeeHistory = false;
    let showExpensesHistory = false;
    let showHistoryCharts = false;
    $: {
        const history = $selectedItem?.core_history?.history_by_year;
        if (history) {
            showHistoryCharts = Object.keys(history).length >= 3;
            showIncomeHistory = Object.values(history).some(yearData => 
                (yearData.rental_income_from_commercial_premises !== null && yearData.rental_income_from_commercial_premises !== undefined) ||
                (yearData.rental_income_from_parking_spots !== null && yearData.rental_income_from_parking_spots !== undefined) ||
                (yearData.rental_income_from_rental_apartments !== null && yearData.rental_income_from_rental_apartments !== undefined)
            );
            showLoanFeeHistory = Object.values(history).some(yearData => 
                (yearData.annual_fee_income !== null && yearData.annual_fee_income !== undefined) ||
                (yearData.total_amount_of_loans !== null && yearData.total_amount_of_loans !== undefined) 
            );
            showExpensesHistory = Object.values(history).some(yearData => 
                (yearData.total_maintenance_expenses !== null && yearData.total_maintenance_expenses !== undefined) ||
                (yearData.total_operating_expenses !== null && yearData.total_operating_expenses !== undefined) ||
                (yearData.other_external_costs !== null && yearData.other_external_costs !== undefined)
            );
        } else {
            showIncomeHistory = false;
            showHistoryCharts = false;
        }
    }

    // Function to change the active tab
    function selectTab(tabName) {
        activeTab.set(tabName);
    }

    onMount(async () => {
        // --- Nollställ chatbotten ---
        answer.set("");
        question.set("");
        // --- Slut nollställning ---
        try {
            loading = true;
            
            // Fetch the BRF data using the ID from the URL
            const result = await fetchBrf(id);
            
            if (!result) {
                error = "Kunde inte ladda information om bostadsrättsföreningen.";
                console.error("Failed to load BRF data, result was:", result);
            }
            
            console.log("BRF data loaded:", $selectedItem);
            
            // Once data is loaded, update SEO metadata
            if ($selectedItem && $selectedItem.brf_name) {
                // Update file name for redovisning if needed
                if ($selectedItem.fileName) {
                    redovisning.set($selectedItem.fileName);
                }
                
                // Update page title with BRF name
                document.title = `${$selectedItem.brf_name} | Boanalys`;
                
                // Update meta description with BRF name and more specific information
                let metaDescription = document.querySelector('meta[name="description"]');
                if (!metaDescription) {
                    metaDescription = document.createElement('meta');
                    metaDescription.setAttribute('name', 'description');
                    document.head.appendChild(metaDescription);
                }
                
                // More detailed description with specific data points when available
                let descriptionText = `Detaljerad analys av ${$selectedItem.brf_name}. `;
                
                // Add specific data points if available
                if ($selectedItem.built_year) descriptionText += `Byggår: ${$selectedItem.built_year}. `;
                if ($selectedItem.number_of_apartments) descriptionText += `${$selectedItem.number_of_apartments} lägenheter. `;
                if ($selectedItem.municipality) descriptionText += `Belägen i ${$selectedItem.municipality}. `;
                if ($selectedItem.living_area_sqm) descriptionText += `Total bostadsyta: ${$selectedItem.living_area_sqm} kvm. `;
                
                descriptionText += `Information om ekonomi, avgifter, underhåll och mer för bostadsrättsföreningen.`;
                
                metaDescription.setAttribute('content', descriptionText);
                
                // Update URL to include BRF name (without affecting current page)
                const slug = slugify($selectedItem.brf_name);
                const newUrl = `/brf/${id}/${slug}`;
                
                // Update canonical URL with the SEO-friendly URL
                let canonical = document.querySelector('link[rel="canonical"]');
                if (!canonical) {
                    canonical = document.createElement('link');
                    canonical.setAttribute('rel', 'canonical');
                    document.head.appendChild(canonical);
                }
                canonical.setAttribute('href', `https://boanalys.io${newUrl}`);
                
                // Update the browser URL bar without reloading the page
                if (history && history.replaceState && !window.location.pathname.includes(slug)) {
                    history.replaceState(null, document.title, newUrl);
                }
                
                // Enhanced structured data for the BRF
                const existingScript = document.querySelector('script[data-type="brf-schema"]');
                if (existingScript) {
                    existingScript.remove();
                }
                
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.setAttribute('data-type', 'brf-schema');
                
                // Create more detailed structured data
                const structuredData = {
                    "@context": "https://schema.org",
                    "@type": "RealEstateOrganization",
                    "name": $selectedItem.brf_name,
                    "description": `Bostadsrättsförening: ${$selectedItem.brf_name}. ${$selectedItem.about || ''}`,
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": $selectedItem.municipality || "",
                        "addressCountry": "SE"
                    },
                    // Add more detailed information
                    "foundingDate": $selectedItem.registry_date || "",
                    "numberOfEmployees": {
                        "@type": "QuantitativeValue",
                        "value": $selectedItem.number_of_apartments || 0,
                        "unitText": "lägenheter"
                    },
                    "areaServed": {
                        "@type": "Place",
                        "name": $selectedItem.municipality || "Sverige"
                    },
                    // Add data for each BRF specific property that might be valuable
                    ...$selectedItem.org_number ? {"taxID": $selectedItem.org_number} : {},
                    ...$selectedItem.built_year ? {"yearBuilt": $selectedItem.built_year} : {},
                    // Add FAQs if you have common questions for this BRF
                    "mainEntityOfPage": {
                        "@type": "WebPage",
                        "@id": `https://boanalys.io/brf/${id}/${slug}`
                    }
                };
                
                script.textContent = JSON.stringify(structuredData);
                document.head.appendChild(script);
                
                // Add a visible data section for LLMs to parse key facts
                const dataRichSectionEl = document.createElement('div');
                dataRichSectionEl.classList.add('data-rich-section');
                dataRichSectionEl.style.display = 'none'; // Hidden from users
                
                // Create content with structured, clear data points
                let llmContent = `
                <div itemscope itemtype="https://schema.org/RealEstateOrganization">
                    <h2 itemprop="name">Nyckeldata för ${$selectedItem.brf_name}</h2>
                    <ul>
                        <li>Organisationsnummer: <span itemprop="taxID">${$selectedItem.org_number || 'Ej tillgängligt'}</span></li>
                        <li>Byggår: <span itemprop="yearBuilt">${$selectedItem.built_year || 'Ej tillgängligt'}</span></li>
                        <li>Antal lägenheter: <span itemprop="numberOfUnits">${$selectedItem.number_of_apartments || 'Ej tillgängligt'}</span></li>
                        <li>Total bostadsyta: <span itemprop="floorSize">${$selectedItem.living_area_sqm || 'Ej tillgängligt'} kvm</span></li>
                        <li>Kommun: <span itemprop="location">${$selectedItem.municipality || 'Ej tillgängligt'}</span></li>
                        <li>Äger marken: <span>${$selectedItem.owns_the_land ? 'Ja' : 'Nej'}</span></li>
                        <li>Belåning per kvadratmeter: <span>${$selectedItem.total_amount_of_loans && $selectedItem.living_area_sqm ? 
                            Math.round($selectedItem.total_amount_of_loans / $selectedItem.living_area_sqm) + ' kr/kvm' : 'Ej tillgängligt'}</span></li>
                    </ul>
                </div>
                `;
                
                dataRichSectionEl.innerHTML = llmContent;
                document.body.appendChild(dataRichSectionEl);
            } else {
                console.error("selectedItem is null or missing name property after fetchBrf completed");
            }
        } catch (err) {
            console.error("Error in BRF page onMount:", err);
            error = "Ett fel uppstod vid laddning av information.";
        } finally {
            loading = false;
            // Scroll to top on load
            window.scrollTo(0, 0);
        }
    });
</script>

<TopBar />
<main style="overflow-x: hidden; max-width: 1600px; height: 101%;">
    <Header />
    <Search />
    
    {#if loading}
        <div class="loading-container" style="width: 250px; margin-right: auto; margin-left: auto; margin-bottom: 80px; margin-top: 80px;">
            <div class="about-text">
                <p>Laddar information...</p>
                <div style="height: 60px;margin-bottom: 30px;" class="question">
                    <Logotype />
                </div>
            </div>
        </div>
    {:else if error}
        <div class="error-container">
            <p>{error}</p>
            <p>Försök igen senare eller kontakta support.</p>
        </div>
    {:else if $selectedItem}
    <!-- New BRF Header Card -->
    <div class="brf-header-card">
        <div class="brf-title-section">
            <div class="brf-icon-container">
                <i class="fas fa-building fa-2x"></i>
            </div>
            <h3 class="brf-name-header">{$selectedItem.brf_name}</h3>
        </div>
        <nav class="brf-nav">
            <button 
                class="brf-nav-item" 
                class:active={$activeTab === 'allmant'} 
                on:click={() => selectTab('allmant')}>
                Allmänt
            </button>
            <button 
                class="brf-nav-item" 
                class:active={$activeTab === 'stadgar'} 
                on:click={() => selectTab('stadgar')}>
                Stadgar
            </button>
            <button 
                class="brf-nav-item" 
                class:active={$activeTab === 'energi'} 
                on:click={() => selectTab('energi')}>
                Energi
            </button>
        </nav>
    </div>
        
    <div class="analyze-info">
        {#if $activeTab === 'allmant'}
            <div class="container">
                {#if $selectedItem?.analyzed}
                <General />
                <Chatbot />
                <Rating />
                
                {#if $selectedItem?.latitude && $selectedItem?.longitude}
                    <div class="property-map-wrapper">    
                        <div class="property-column">
                            <Property />
                        </div>
                        <div class="map-column">
                            <PropertyMap />
                        </div>
                    </div>
                {:else} 
                    <Property /> 
                {/if}

                <Maintenance />
                <EconomyAndIncomes />
                <Company />
                {#if showHistoryCharts}
                    <div class="history-charts-container">
                        {#if showExpensesHistory}
                            <ExpensesHistory />
                        {/if}
                        {#if showIncomeHistory}
                            <IncomeHistory />
                        {/if}
                        {#if showLoanFeeHistory}
                            <LoanFeeHistory />
                        {/if}
                    </div>
                {/if}
                {:else}
                <div class="unanalysed-section">
                    <Company />
                    <UploadRedovisning />
                </div>
                {/if}
                {#if $currentUser?.subscription_tier === 'Pro' || $currentUser?.subscription_tier === 'Plus'}
                    {#if !$selectedItem?.analyzed === true && $selectedItem?.redovisning_file_name != null && $selectedItem?.redovisning_file_name !== ""}
                    <div class="pdf-container">
                        <h3>Årsredovisning</h3>
                        <iframe class="iframe" title="redovisning" src={getFileName($selectedItem.redovisning_file_name)} frameborder=0 ></iframe>
                    </div>
                    {/if}
                    {#if $selectedItem?.analyzed === true && $selectedItem?.file_name != null && $selectedItem?.file_name !== ""}
                    <div class="pdf-container">
                        <h3>Årsredovisning</h3>
                        <iframe class="iframe" title="redovisning" src={getFileName($selectedItem.file_name)} frameborder=0 ></iframe>
                        </div>
                    {/if}
                {/if}
            </div>
        {/if}
        
        {#if $activeTab === 'stadgar'}
            <div class="full-width-section stadgar-content-tab">
                {#if $selectedItem?.stadgar_id !== null}
                <div class="container">
                    <Membership />
                    <Fees />
                    <OwnershipTransfer />
                    <OwnerRights />
                    <RightsObligations />
                    <ForverkanOwner />
                    <Styrning />
                    <Stamma />
                    <MaintenancePlan />
                    <RevenueDisposition />
                    <Liquidation />
                    <StadgarEtc />
                </div>
                {/if}
                <AnalyzeStadgar />
            </div>
        {/if}
        
        {#if $activeTab === 'energi'}
            <div class="full-width-section energi-content-tab">
                {#if $selectedItem?.energy_declaration_id !== null}
                <div class="container">
                    <EnergyGeneral />
                    <BuildingInfo />
                    <SunEnergy />
                    <Heating />
                    <Suggestions />
                </div>
                {/if}
                <AnalyzeEnergy />
            </div>
        {/if}
    </div>
    {:else}
        <div class="error-container">
            <p>Ingen information hittades för denna bostadsrättsförening.</p>
        </div>
    {/if}
</main>

<footer>
    <p>©Boanalys. All rights reserved.</p>
    <p><Link to="/about#kontakt-section">Kontakta oss</Link></p>
</footer>

<style>
    main {
        max-width: 1800px;
        margin: 0 auto;
    }
    
    /* Base container styles */
    .container {
        display: grid;
        gap: 20px;
    }

    /* Desktop layout (992px and up) */
    @media (min-width: 992px) {
        .container {
            grid-template-columns: 1fr 2fr 1fr; /* First row: 1:2:1 */
        }

        /* First row components */
        .container > :global(.box:nth-child(1)), /* General */
        .container > :global(.box:nth-child(2)), /* Chatbot */
        .container > :global(.box:nth-child(3))  /* Rating */
        {
            width: 100%;
        }

        /* Property and Map wrapper */
        .property-map-wrapper {
            display: flex;
            gap: 20px;
            grid-column: 1 / -1;
        }

        .property-map-wrapper > .property-column,
        .property-map-wrapper > .map-column {
            flex: 1;
        }
    }

    /* Tablet layout (768px to 991px) */
    @media (min-width: 768px) and (max-width: 991px) {
        .container {
            grid-template-columns: 1fr;
        }

        .property-map-wrapper {
            display: flex;
            gap: 20px;
        }

        .property-map-wrapper > .property-column,
        .property-map-wrapper > .map-column {
            flex: 1;
        }
    }

    /* Mobile layout (below 768px) */
    @media (max-width: 767px) {
        .container {
            grid-template-columns: 1fr;
        }

        .property-map-wrapper {
            display: flex;
            flex-direction: column;
            gap: 20px;
        }

        .property-map-wrapper > .property-column,
        .property-map-wrapper > .map-column {
            width: 100%;
        }

        /* Ensure map has a good height on mobile */
        .property-map-wrapper > .map-column > :global(*) {
            height: 300px;
            min-height: 300px;
        }
    }

    /* Common styles for all screen sizes */
    .container > :global(.box),
    .container > :global(app-uploadredovisning) {
        width: 100%;
    }

    /* PDF container always full width */
    .pdf-container {
        grid-column: 1 / -1;
        width: 100%;
    }

    /* Styles for the new BRF header card */
    .brf-header-card {
        background-color: #fff;
        border-radius: 8px;
        padding: 20px;
        padding-bottom: 15px;
        margin: 50px auto 20px auto; /* Keeps top/bottom margin, auto for left/right centers it */
        width: fit-content; /* Makes the card width fit its content */
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        max-width: 700px;
    }

    .brf-title-section {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
    }

    .brf-icon-container {
        margin-right: 15px;
        color: var(--box-title-color); /* Dark blueish color for icon */
    }

    .brf-icon-container .fas { /* If using Font Awesome */
        font-size: 28px; /* Adjust size */
        line-height: 1; /* Ensure proper alignment */
    }

    .brf-name-header {
        font-size: 1.8em; 
        font-weight: bold;
        color: var(--box-title-color); 
        margin: 0; 
    }

    .brf-nav {
        display: flex;
        justify-content: center; /* Center the tab items */
        border-top: 2px solid #eee; /* Light grey line above tabs container */
        padding-top: 5px; /* Add some padding above the tabs */
    }

    .brf-nav-item {
        background: none;
        border: none;
        padding: 10px 15px;
        cursor: pointer;
        font-size: 1em;
        color: #7f8c8d; /* Greyish color for inactive tabs */
        position: relative;
        font-weight: 500;
        margin-right: 10px; /* Spacing between tabs */
    }
    .brf-nav-item:last-child {
        margin-right: 0;
    }

    .brf-nav-item.active {
        color: var(--hover); /* Blue color for active tab text */
        font-weight: bold;
    }

    .brf-nav-item.active::after {
        content: '';
        position: absolute;
        bottom: -1px; /* Align with the nav border-bottom */
        left: 0;
        width: 100%;
        height: 3px;
        background-color: var(--hover); /* Blue underline */
        border-radius: 1.5px; 
    }
    
    /* Hover effects for non-active tabs */
    .brf-nav-item:not(.active):hover {
        color: var(--hover); /* Change text color on hover to match active tab color */
    }

    .brf-nav-item:not(.active)::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -1px; /* Position the underline slightly below the text */
        width: 0;    /* Initial width of the underline is 0 */
        height: 3px; /* Thickness of the underline */
        background-color: var(--hover); /* Underline color, matches active tab */
        transition: width 0.3s ease; /* Smooth transition for the width */
        border-radius: 1.5px; 
    }

    .brf-nav-item:not(.active):hover::after {
        width: 100%; /* Expand underline to full width on hover */
    }

    .analyze-info {
        margin-bottom: 20px;
    }
    
    /* Ensure tab content sections are distinct */
    .analyze-info > .full-width-section {
        margin-top: 0; /* Reset top margin if .analyze-info provides it */
        padding-top: 20px; /* Add padding if needed */
    }

    /* Added style for full-width sections */
    .full-width-section {
        grid-column: 1 / -1; /* Make the section span all columns */
        width: 100%;
        margin-top: 20px;
        margin-bottom: 30px;
        padding-top: 20px;
    }
    
    /* Reset any grid styling in the nested container */
    .full-width-section .container {
        display: block; /* Override grid display */
        width: 100%;
    }
    
    /* Make each component in stadgar/energy sections take full width */
    .full-width-section .container > :global(*) {
        width: 100%;
        margin-bottom: 20px;
    }

    /* Styles for the section when BRF is not analyzed */
    .unanalysed-section {
        grid-column: 1 / -1; /* Span all grid columns */
        display: flex; /* Use flexbox for centering */
        flex-direction: column; /* Stack children vertically */
        align-items: center; /* Center children horizontally */
        gap: 20px; /* Space between children */
        width: 100%; /* Take full width of the grid area */
        margin-top: 20px; /* Add some space above */
        margin-bottom: 30px; /* Add some space below */
    }

    /* Style the components within the unanalysed section */
    .unanalysed-section > :global(*) {
        /* Make each component take roughly half the width on larger screens */
        width: 50%;
        /* Optional: Set a max-width if needed, e.g., for very wide screens */
        /* max-width: 900px; */
        /* Ensure components have a minimum reasonable width */
        min-width: 300px; /* Adjust as needed */
        box-sizing: border-box; /* Ensure padding/border are included in the width */
    }

    /* Adjust width for smaller screens for better layout */
    @media (max-width: 991px) { /* Apply on tablet and potentially wider mobile */
        .unanalysed-section > :global(*) {
            width: 75%; /* Use a larger percentage on medium screens */
        }
    }

    @media (max-width: 767px) { /* Apply on standard mobile */
        .unanalysed-section > :global(*) {
            width: 100%; /* Take full width on small screens */
            min-width: unset; /* Remove min-width constraint */
        }
    }

    /* Style the history charts container */
    .history-charts-container {
        grid-column: 1 / -1; /* Span full width of the parent grid */
        display: flex;
        flex-wrap: wrap; /* Allow items to wrap to the next line */
        justify-content: center; /* Center items horizontally */
        gap: 20px; /* Space between charts */
    }

    /* Style the individual history chart components within the container */
    .history-charts-container > :global(*) {
        flex-grow: 1;
        /* Each item tries to be 50% wide, minus half the gap */
        flex-basis: calc(50% - 10px);
        /* A reasonable minimum width */
        min-width: 350px;
        /* Set max width based on user request */
        max-width: 800px; 
        box-sizing: border-box;
    }

    /* On smaller screens, make them full width */
    @media (max-width: 767px) {
        .history-charts-container > :global(*) {
            flex-basis: 100%;
            min-width: 280px; /* Smaller min-width for mobile */
            max-width: 100%; /* Override max-width for mobile */
        }
    }
</style> 